import { Fragment, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useClients, useDeleteClient } from "@/hooks/useClients";
import { useFunds } from "@/hooks/useFunds";
import { useInvestments, useUpsertInvestment } from "@/hooks/useInvestments";
import { useAllFundAssets } from "@/hooks/useFundAssets";
import { useAllCashflows } from "@/hooks/useCashflows";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { InteractionsSection } from "@/components/InteractionsSection";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const brl = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

function CashflowTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string | number;
}) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value ?? 0), 0);
  return (
    <div className="rounded-lg border border-border bg-card shadow-md p-3 text-xs space-y-1 min-w-[180px]">
      <p className="font-semibold text-sm mb-2">Year {label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex justify-between gap-4">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium">{brl(p.value)}</span>
        </div>
      ))}
      <div className="flex justify-between gap-4 border-t pt-1 mt-1 font-semibold">
        <span>Total</span>
        <span>{brl(total)}</span>
      </div>
    </div>
  );
}

const FUND_COLORS = [
  "#1e3a5f", "#1e5f8f", "#1d7fc4", "#3b9fd6",
  "#60b8e0", "#8dd0ec", "#b3e2f5", "#d6f0fb",
];
import { ArrowLeft, Mail, MapPin, Pencil, Phone, Trash2, User } from "lucide-react";
import { formatBRL, formatUSD } from "@/lib/utils";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: clients } = useClients();
  const { data: funds } = useFunds();
  const { data: investments } = useInvestments();
  const { data: allAssets } = useAllFundAssets();
  const { data: allCashflows } = useAllCashflows();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;

  const upsert = useUpsertInvestment();
  const deleteClient = useDeleteClient();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Investment inline edit
  const [editing, setEditing] = useState<{ fundId: string; field: "brl" | "usd" } | null>(null);
  const [editValue, setEditValue] = useState("");

  const client = clients?.find((c) => c.id === id);
  const myInvestments = (investments ?? []).filter((i) => i.client_id === id);
  const totalBRL = myInvestments.reduce((s, i) => s + Number(i.amount_brl), 0);
  const totalUSD = myInvestments.reduce((s, i) => s + Number(i.amount_usd), 0);

  // Fund totals (for participation %)
  const fundTotals = useMemo(() => {
    const map = new Map<string, number>();
    (investments ?? []).forEach((i) => {
      const fund = funds?.find((f) => f.id === i.fund_id);
      const brl = Number(i.amount_brl) + Number(fund?.usdbrl_rate ?? 0) * Number(i.amount_usd);
      map.set(i.fund_id, (map.get(i.fund_id) ?? 0) + brl);
    });
    return map;
  }, [investments, funds]);

  // Client cashflow chart: one key per fund, weighted by participation
  const { cashflowChartData, cashflowFunds } = useMemo(() => {
    if (!myInvestments.length || !allAssets || !allCashflows || !funds)
      return { cashflowChartData: [], cashflowFunds: [] };

    const investedFundIds = new Set(myInvestments.map((i) => i.fund_id));
    const clientPct = new Map<string, number>();
    myInvestments.forEach((i) => {
      const fund = funds.find((f) => f.id === i.fund_id);
      const clientBrl = Number(i.amount_brl) + Number(fund?.usdbrl_rate ?? 0) * Number(i.amount_usd);
      const total = fundTotals.get(i.fund_id) ?? 0;
      clientPct.set(i.fund_id, total > 0 ? clientBrl / total : 0);
    });

    // investedFunds ordered by name for stable color assignment
    const investedFunds = funds.filter((f) => investedFundIds.has(f.id));

    // map: year → { [fundName]: amount }
    const map = new Map<number, Record<string, number>>();
    allCashflows.forEach((c) => {
      const asset = allAssets.find((a) => a.id === c.fund_asset_id);
      if (!asset || !investedFundIds.has(asset.fund_id)) return;
      const fund = funds.find((f) => f.id === asset.fund_id);
      if (!fund) return;
      const pct = clientPct.get(asset.fund_id) ?? 0;
      const prev = map.get(c.year) ?? {};
      map.set(c.year, {
        ...prev,
        [fund.name]: (prev[fund.name] ?? 0) + Number(c.amount ?? 0) * pct,
      });
    });

    const chartData = Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([year, byFund]) => ({ year, ...byFund }));

    return { cashflowChartData: chartData, cashflowFunds: investedFunds };
  }, [myInvestments, allAssets, allCashflows, funds, fundTotals]);

  if (!client) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="IR Jus Capital" subtitle="Client not found" />
      </div>
    );
  }

  // Pareia POC/E-mail/Phone pelo índice (arrays paralelos) sem descartar posições
  // vazias, pra não desalinhar quem-é-quem quando algum contato não tem telefone.
  const contactRowCount = Math.max(
    client.person_of_contact.length,
    client.email.length,
    client.phone.length
  );
  const contactRows = Array.from({ length: contactRowCount }, (_, i) => ({
    name: (client.person_of_contact[i] ?? "").trim(),
    email: (client.email[i] ?? "").trim(),
    phone: (client.phone[i] ?? "").trim(),
  }));

  const cellValue = (fundId: string, field: "brl" | "usd") => {
    const inv = myInvestments.find((i) => i.fund_id === fundId);
    return inv ? Number(inv[field === "brl" ? "amount_brl" : "amount_usd"]) : 0;
  };

  const startEdit = (fundId: string, field: "brl" | "usd") => {
    setEditing({ fundId, field });
    setEditValue(String(cellValue(fundId, field) || ""));
  };

  const commit = async () => {
    if (!editing || !id) return;
    const num = parseFloat(editValue) || 0;
    const inv = myInvestments.find((i) => i.fund_id === editing.fundId);
    try {
      await upsert.mutateAsync({
        client_id: id,
        fund_id: editing.fundId,
        amount_brl: editing.field === "brl" ? num : Number(inv?.amount_brl ?? 0),
        amount_usd: editing.field === "usd" ? num : Number(inv?.amount_usd ?? 0),
      });
    } catch {
      toast.error("Error saving");
    }
    setEditing(null);
  };

  const handleDelete = async () => {
    try {
      await deleteClient.mutateAsync(id!);
      toast.success("Client removed");
      navigate("/clients");
    } catch {
      toast.error("Error removing client");
    }
    setDeleteOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={client.name}
        subtitle={`${client.origin} · ${client.type}`}
        actions={
          <div className="flex gap-2">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                  className="gap-2"
                >
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                  className="gap-2 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/clients")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Clients
            </Button>
          </div>
        }
        showBack={false}
      />
      <div className="container mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-display font-semibold">{client.name}</h1>

        {/* Investments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Investments BRL</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(funds ?? []).map((f) => {
                const isEditing = editing?.fundId === f.id && editing.field === "brl";
                const val = cellValue(f.id, "brl");
                const fundTotal = fundTotals.get(f.id) ?? 0;
                const fund = funds?.find((x) => x.id === f.id);
                const clientBrl = val + Number(fund?.usdbrl_rate ?? 0) * cellValue(f.id, "usd");
                const pct = fundTotal > 0 ? (clientBrl / fundTotal) * 100 : 0;
                return (
                  <div key={f.id} className="flex justify-between items-center text-sm gap-2">
                    <span className="flex items-center gap-2">
                      {f.name}
                      {pct > 0 && (
                        <span className="text-xs text-muted-foreground">({pct.toFixed(1)}%)</span>
                      )}
                    </span>
                    {isEditing ? (
                      <Input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commit();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        className="h-7 text-right w-36"
                      />
                    ) : (
                      <span
                        className="font-medium cursor-pointer hover:underline decoration-dotted"
                        onClick={() => startEdit(f.id, "brl")}
                      >
                        {formatBRL(val) || "—"}
                      </span>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between border-t pt-2 mt-2 font-semibold">
                <span>Total</span>
                <span>{formatBRL(totalBRL)}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Investments USD</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(funds ?? []).map((f) => {
                const isEditing = editing?.fundId === f.id && editing.field === "usd";
                const val = cellValue(f.id, "usd");
                return (
                  <div key={f.id} className="flex justify-between items-center text-sm gap-2">
                    <span>{f.name}</span>
                    {isEditing ? (
                      <Input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commit();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        className="h-7 text-right w-36"
                      />
                    ) : (
                      <span
                        className="font-medium cursor-pointer hover:underline decoration-dotted"
                        onClick={() => startEdit(f.id, "usd")}
                      >
                        {formatUSD(val) || "—"}
                      </span>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between border-t pt-2 mt-2 font-semibold">
                <span>Total</span>
                <span>{formatUSD(totalUSD)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cashflow chart */}
        {cashflowChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Projected Cashflow</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={cashflowChartData}
                  margin={{ top: 8, right: 16, left: 24, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 12 }}
                    className="fill-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis hide />
                  <Tooltip content={<CashflowTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }}
                  />
                  {cashflowFunds.map((fund, i) => (
                    <Bar
                      key={fund.id}
                      dataKey={fund.name}
                      stackId="a"
                      fill={FUND_COLORS[i % FUND_COLORS.length]}
                      radius={i === cashflowFunds.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Contato */}
        <Card>
          <CardHeader>
            <CardTitle>Contact</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" /> POC Investor
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4" /> E-mail
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4" /> Phone
            </div>

            {contactRows.length === 0 && (
              <p className="text-muted-foreground md:col-span-3">—</p>
            )}
            {contactRows.map((row, i) => (
              <Fragment key={i}>
                <p>{row.name || "—"}</p>
                <p>{row.email || "—"}</p>
                <p>{row.phone || "—"}</p>
              </Fragment>
            ))}

            {client.role && (
              <Badge variant="outline" className="w-fit md:col-span-3">{client.role}</Badge>
            )}

            <div className="md:col-span-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MapPin className="h-4 w-4" /> Address
              </div>
              <p className="whitespace-pre-line">{client.address || "—"}</p>
            </div>

            <div className="md:col-span-3">
              <div className="text-muted-foreground mb-1">POC Jus</div>
              <p>{client.responsavel_jusc}</p>
            </div>
          </CardContent>
        </Card>

        {/* Interactions */}
        <InteractionsSection clientId={id!} isAdmin={isAdmin} />
      </div>

      {/* Edit dialog */}
      <ClientFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        client={client}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove client?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All client investments and interactions
              will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
